import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../../../App";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Checkbox } from "../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../ui/accordion";
import { toast } from "sonner";
import { Loader2, RefreshCw, Save, RotateCcw, Plus, Pencil, Trash2 } from "lucide-react";
import SortableFieldList from "./SortableFieldList";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const deep = (o) => JSON.parse(JSON.stringify(o));

const HEADER_OPTIONS = [
  ["show_logo", "Company logo"],
  ["show_business_name", "Business name"],
  ["show_location_name", "Location name"],
  ["show_business_address", "Business address"],
  ["show_business_phone", "Business phone"],
  ["show_business_email", "Business email"],
  ["show_technician_name", "Technician name"],
  ["show_period", "Report date range"],
  ["show_generated_date", "Generated date"],
  ["accent_bar", "Gold accent bar"],
];

const FOOTER_OPTIONS = [
  ["show_business_name", "Business name"],
  ["show_page_numbers", "Page numbers (Page X of Y)"],
  ["show_generated_date", "Generated date"],
  ["confidential_notice", "Confidential notice"],
];

const CheckRow = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 py-1 cursor-pointer">
    <Checkbox checked={!!checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testId} />
    <span className="text-sm text-foreground">{label}</span>
  </label>
);

const TechReportTemplateEditor = ({ template, registry, onSaved, onClose }) => {
  const [name, setName] = useState(template.name);
  const [config, setConfig] = useState(() => deep({ ...registry.default_config, ...template.config }));
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTech, setPreviewTech] = useState("all");
  const [previewRange, setPreviewRange] = useState("this_month");
  const [blockDraft, setBlockDraft] = useState(null); // {id?, title, text, align}
  const debounceRef = useRef(null);
  const urlRef = useRef(null);

  const sectionRegistry = useMemo(() => {
    const m = {};
    (registry.sections || []).forEach((s) => { m[s.id] = s.label; });
    return m;
  }, [registry]);
  const colRegistry = useMemo(() => Object.fromEntries((registry.job_columns || []).map((c) => [c.key, c.label])), [registry]);
  const sumRegistry = useMemo(() => Object.fromEntries((registry.summary_fields || []).map((c) => [c.key, c.label])), [registry]);
  const balRegistry = useMemo(() => Object.fromEntries((registry.balance_columns || []).map((c) => [c.key, c.label])), [registry]);

  const set = (patch) => setConfig((c) => ({ ...c, ...patch }));
  const setNested = (key, patch) => setConfig((c) => ({ ...c, [key]: { ...c[key], ...patch } }));

  const fetchPreview = useCallback(async (cfg, tech, range) => {
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/report-templates/preview`,
        { config: cfg, technician_id: tech === "all" ? null : tech, date_filter: range },
        { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setPreviewUrl(url);
    } catch {
      toast.error("Preview failed — check the template configuration");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // Live preview: debounce re-render after any config change.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPreview(config, previewTech, previewRange), 1200);
    return () => clearTimeout(debounceRef.current);
  }, [config, previewTech, previewRange, fetchPreview]);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/report-templates/${template.template_id}`,
        { name, config }, auth());
      toast.success("Template saved");
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (!window.confirm("Reset this template to the Fieldz AI default layout? Your customizations will be replaced (nothing is saved until you press Save).")) return;
    setConfig(deep(registry.default_config));
    toast.info("Layout reset to Fieldz AI default — press Save to keep it");
  };

  const saveBlock = () => {
    const d = blockDraft;
    if (!d.title && !d.text) { setBlockDraft(null); return; }
    setConfig((c) => {
      const sections = [...c.sections];
      if (d.id) {
        const i = sections.findIndex((s) => s.id === d.id);
        if (i >= 0) sections[i] = { ...sections[i], title: d.title, text: d.text, align: d.align };
      } else {
        sections.push({ id: `custom:${Math.random().toString(36).slice(2, 10)}`, visible: true, title: d.title, text: d.text, align: d.align });
      }
      return { ...c, sections };
    });
    setBlockDraft(null);
  };

  const removeBlock = (id) =>
    setConfig((c) => ({ ...c, sections: c.sections.filter((s) => s.id !== id) }));

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[1280px] w-[96vw] h-[92vh] p-0 gap-0 flex flex-col" data-testid="template-editor-dialog">
        <div className="flex items-center gap-3 border-b border-theme px-4 py-3">
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-base">Technician Report Template</DialogTitle>
            <DialogDescription className="text-xs">Presentation only — earnings, tax and settlement math never change.</DialogDescription>
          </div>
          <Button variant="outline" size="sm" onClick={resetToDefault} data-testid="reset-default-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to Fieldz AI Default
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/80" data-testid="save-template-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save Template
          </Button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* ── Left: controls ── */}
          <div className="w-[430px] shrink-0 overflow-y-auto border-r border-theme p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Template name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="input-glass mt-1" data-testid="template-name-input" />
              </div>
              <div>
                <Label className="text-xs">Report title (on PDF)</Label>
                <Input value={config.title || ""} onChange={(e) => set({ title: e.target.value })} className="input-glass mt-1" data-testid="report-title-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Orientation</Label>
                <Select value={config.layout?.orientation || "landscape"} onValueChange={(v) => setNested("layout", { orientation: v })}>
                  <SelectTrigger className="input-glass mt-1 h-9" data-testid="orientation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landscape">Landscape (recommended)</SelectItem>
                    <SelectItem value="portrait">Portrait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Paper size</Label>
                <Select value={config.layout?.paper || "letter"} onValueChange={(v) => setNested("layout", { paper: v })}>
                  <SelectTrigger className="input-glass mt-1 h-9" data-testid="paper-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="letter">Letter</SelectItem>
                    <SelectItem value="a4">A4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Accordion type="multiple" defaultValue={["sections"]} className="w-full">
              <AccordionItem value="header">
                <AccordionTrigger className="text-sm py-2" data-testid="acc-header">Header</AccordionTrigger>
                <AccordionContent>
                  {HEADER_OPTIONS.map(([k, label]) => (
                    <CheckRow key={k} checked={config.header?.[k]} label={label} testId={`header-${k}`}
                      onChange={(v) => setNested("header", { [k]: v })} />
                  ))}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="sections">
                <AccordionTrigger className="text-sm py-2" data-testid="acc-sections">Sections &amp; Order</AccordionTrigger>
                <AccordionContent>
                  <p className="text-xs text-muted-foreground mb-2">Drag to reorder. Toggle to show/hide on the PDF (data is never deleted).</p>
                  <SortableFieldList
                    items={config.sections}
                    registry={sectionRegistry}
                    renamable={false}
                    onChange={(sections) => set({ sections })}
                    testPrefix="section"
                    rowExtras={(item) => item.id?.startsWith("custom:") ? (
                      <span className="flex">
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
                          onClick={() => setBlockDraft({ id: item.id, title: item.title || "", text: item.text || "", align: item.align || "left" })}
                          data-testid={`edit-block-${item.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-500"
                          onClick={() => removeBlock(item.id)} data-testid={`delete-block-${item.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </span>
                    ) : null}
                  />
                  <Button variant="outline" size="sm" className="mt-2 w-full" data-testid="add-text-block-btn"
                    onClick={() => setBlockDraft({ title: "", text: "", align: "left" })}>
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Custom Text Block
                  </Button>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="summary">
                <AccordionTrigger className="text-sm py-2" data-testid="acc-summary">Summary Fields</AccordionTrigger>
                <AccordionContent>
                  <p className="text-xs text-muted-foreground mb-2">Pick totals, rename labels, drag to reorder.</p>
                  <SortableFieldList items={config.summary_fields} registry={sumRegistry}
                    onChange={(summary_fields) => set({ summary_fields })} testPrefix="summary" />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="columns">
                <AccordionTrigger className="text-sm py-2" data-testid="acc-columns">Job Table Columns</AccordionTrigger>
                <AccordionContent>
                  <p className="text-xs text-muted-foreground mb-2">
                    Show/hide, rename (e.g. "Technician Commission" → "Tech Earnings") and reorder.
                    Widths &amp; font auto-adjust; many columns work best in landscape.
                  </p>
                  <SortableFieldList items={config.job_columns} registry={colRegistry}
                    onChange={(job_columns) => set({ job_columns })} testPrefix="jobcol" />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="balances">
                <AccordionTrigger className="text-sm py-2" data-testid="acc-balances">Settlement Summary Columns</AccordionTrigger>
                <AccordionContent>
                  <SortableFieldList items={config.balance_columns} registry={balRegistry}
                    onChange={(balance_columns) => set({ balance_columns })} testPrefix="balcol" />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="signature">
                <AccordionTrigger className="text-sm py-2" data-testid="acc-signature">Signature</AccordionTrigger>
                <AccordionContent>
                  <p className="text-xs text-muted-foreground mb-1">Enable the Signature section in "Sections &amp; Order" to print it.</p>
                  <CheckRow checked={config.signature?.technician} label="Technician signature line" testId="sig-technician"
                    onChange={(v) => setNested("signature", { technician: v })} />
                  <CheckRow checked={config.signature?.manager} label="Manager signature line" testId="sig-manager"
                    onChange={(v) => setNested("signature", { manager: v })} />
                  <CheckRow checked={config.signature?.date_signed} label="Date signed line" testId="sig-date"
                    onChange={(v) => setNested("signature", { date_signed: v })} />
                  <Label className="text-xs mt-2 block">Acknowledgment text</Label>
                  <Textarea value={config.signature?.acknowledgment || ""} rows={2} className="input-glass mt-1 text-xs"
                    onChange={(e) => setNested("signature", { acknowledgment: e.target.value })} data-testid="sig-ack-text" />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="footer">
                <AccordionTrigger className="text-sm py-2" data-testid="acc-footer">Footer</AccordionTrigger>
                <AccordionContent>
                  {FOOTER_OPTIONS.map(([k, label]) => (
                    <CheckRow key={k} checked={config.footer?.[k]} label={label} testId={`footer-${k}`}
                      onChange={(v) => setNested("footer", { [k]: v })} />
                  ))}
                  <Label className="text-xs mt-2 block">Custom footer text</Label>
                  <Input value={config.footer?.custom_text || ""} className="input-glass mt-1"
                    onChange={(e) => setNested("footer", { custom_text: e.target.value })} data-testid="footer-custom-text" />
                  <div className="mt-3 space-y-1">
                    <CheckRow checked={config.show_tax_note} label="Tax note under summary (when tax charged)" testId="show-tax-note"
                      onChange={(v) => set({ show_tax_note: v })} />
                    <CheckRow checked={config.show_split_note} label="Split-job footnote under jobs table" testId="show-split-note"
                      onChange={(v) => set({ show_split_note: v })} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* ── Right: live PDF preview ── */}
          <div className="flex-1 flex flex-col min-w-0 bg-theme-surface/50">
            <div className="flex items-center gap-2 border-b border-theme px-3 py-2">
              <span className="text-xs font-medium text-muted-foreground">Live preview (real PDF engine)</span>
              <Select value={previewTech} onValueChange={setPreviewTech}>
                <SelectTrigger className="h-7 w-[170px] text-xs input-glass" data-testid="preview-tech-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Technicians</SelectItem>
                  {(registry.technicians || []).map((t) => (
                    <SelectItem key={t.technician_id} value={t.technician_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={previewRange} onValueChange={setPreviewRange}>
                <SelectTrigger className="h-7 w-[130px] text-xs input-glass" data-testid="preview-range-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="this_year">This Year</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-7 ml-auto" disabled={previewLoading}
                onClick={() => fetchPreview(config, previewTech, previewRange)} data-testid="refresh-preview-btn">
                {previewLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Preview PDF
              </Button>
            </div>
            <div className="flex-1 min-h-0">
              {previewUrl ? (
                <iframe title="PDF preview" src={`${previewUrl}#zoom=90`} className="w-full h-full" data-testid="preview-iframe" />
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  {previewLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : "Preview will appear here"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Custom text block editor */}
        {blockDraft && (
          <Dialog open onOpenChange={(v) => { if (!v) setBlockDraft(null); }}>
            <DialogContent className="max-w-md" data-testid="block-editor-dialog">
              <DialogTitle className="text-base">{blockDraft.id ? "Edit Text Block" : "Add Text Block"}</DialogTitle>
              <DialogDescription className="text-xs">Use **bold** for bold text. Line breaks are kept. Drag the block anywhere in "Sections &amp; Order".</DialogDescription>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Title (optional)</Label>
                  <Input value={blockDraft.title} className="input-glass mt-1" placeholder="e.g. Payment Terms"
                    onChange={(e) => setBlockDraft({ ...blockDraft, title: e.target.value })} data-testid="block-title-input" />
                </div>
                <div>
                  <Label className="text-xs">Text</Label>
                  <Textarea value={blockDraft.text} rows={5} className="input-glass mt-1 text-sm"
                    placeholder={"e.g. Payments are settled every Friday.\n**Questions?** Contact the office."}
                    onChange={(e) => setBlockDraft({ ...blockDraft, text: e.target.value })} data-testid="block-text-input" />
                </div>
                <div>
                  <Label className="text-xs">Alignment</Label>
                  <Select value={blockDraft.align} onValueChange={(v) => setBlockDraft({ ...blockDraft, align: v })}>
                    <SelectTrigger className="input-glass mt-1 h-9" data-testid="block-align-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setBlockDraft(null)}>Cancel</Button>
                  <Button size="sm" onClick={saveBlock} data-testid="save-block-btn">Save Block</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TechReportTemplateEditor;
